import {
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOf,
  shape,
  string,
} from 'prop-types';
import React, { useEffect, useState } from 'react';
import { GoInfo } from 'react-icons/go';
import { Alert, Col, Collapse, FormGroup, Input, Label, Row } from 'reactstrap';

import CreativeAssociations from '../../campaign-line-creatives/CreativeAssociations.container';
import { merchantExclusivityOptions } from '../../campaigns/constants';
import CurrencyFormat from '../../generic/CurrencyFormat';
import DateRange from '../../generic/DateRange';
import SingleSelect from '../../generic/SingleSelect';
import TagInput from '../../generic/TagInput';
import { getTooltipLabelWithText } from '../../generic/Tooltip';
import {
  getOperativeSystem,
  isCollectionIdsAllowed,
  isContentIdsAllowed,
  isOfferTypeAllowingPropositions,
  offerTypeOptions,
  offerTypes,
  OPERATIVE_SYSTEM_TYPES as operativeSystemsTypes,
  productCodes,
  propositionOptions,
} from '../../products/constants';
import { isCCP, isOffsite } from '../../products/utils';
import {
  campaignProductMetaShape,
  clCountryLineShape,
  highlightedSkusShape,
  offSiteMinBudgetShape,
} from '../../prop-types';
import OffSiteMinBudgetCalculator from '../../quote-editor/OffSiteMinSpendCalculator';
import { PricingModels } from '../../quotes/constants';
import { COLORS } from '../../utils/constants';
import FEATURE_FLAGS, { useSafeFlag } from '../../utils/flags';
import { campaignLineStatuses, complianceStatuses } from '../constants';
import HELP_TEXTS from '../utils/help-texts';
import BudgetDetailsPopover from './BudgetDetailsPopover';
import CampaignLineHeaderActions from './CampaignLineHeaderActions';
import CampaignLinePushButton from './CampaignLinePushButton';
import CampaignLineStatus from './CampaignLineStatus';
import CollectionIds from './CollectionIds';
import ComplianceStatus from './ComplianceStatus';
import CountryLine from './CountryLine';
import CreativeBudgetBreakdown from './CreativeBudgetBreakdown';
import CreativeEnhancedProductShootingBudgetBreakdown from './CreativeEnhancedProductShootingBudgetBreakdown';
import CreativeLandingPageBudgetBreakdown from './CreativeLandingPageBudgetBreakdown';
import HighlightedSkusWarning from './HighlightedSkusWarning';
import {
  ProductLockButton,
  ProductStatusChangeButton,
} from './ProductStatusChangeButton';

function renderHighlightedSKUsInput(
  productCode,
  highlightedSkus,
  onChange,
  isDisabled,
  isSkuInput,
  isCCPproduct,
  setOverCCPLimit,
  isDraftCampaignEnabled,
) {
  return (
    <TagInput
      id={`ca-highlightedSkus.${productCode}`}
      name={`highlightedSkus.${productCode}`}
      value={highlightedSkus[productCode] || []}
      onChange={onChange}
      readOnly={isDisabled}
      allowCopy
      isSkuInput={isSkuInput}
      isCCP={isDraftCampaignEnabled ? false : isCCPproduct}
      setOverCCPLimit={isDraftCampaignEnabled ? false : setOverCCPLimit}
    />
  );
}

function renderCountryLines(
  expanded,
  countries,
  name,
  onChange,
  disableTextAssets,
) {
  return (
    <Collapse isOpen={expanded}>
      {countries.map((country, idx) => (
        <CountryLine
          {...country}
          key={idx} // eslint-disable-line react/no-array-index-key
          disableTextAssets={disableTextAssets}
        />
      ))}
    </Collapse>
  );
}

function renderNumberOfURL(index, name, value, onChange) {
  return (
    <Row style={{ marginTop: '1rem' }}>
      <Col sm={3} />
      <Label sm={2} for="numberOfURLs" className="c-campaign-line__label">
        Number of URLs
      </Label>
      <Col sm={7}>
        <Row>
          <Col sm={3}>
            <Input
              name={`productMetas[${index}].${name}`}
              type="number"
              className="d-inline form-control p-0 pl-2 h-auto"
              value={value}
              style={{ width: '110px' }}
              onChange={onChange}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

function shouldRenderPushButton(operativeSystem, isDraftCampaignEnabled) {
  const operativeSystemTypes = getOperativeSystem(isDraftCampaignEnabled);
  const ccp = operativeSystemTypes.ccp.system;
  const creativeFactory = operativeSystemTypes.creativeFactory.system;
  const offsite = operativeSystemTypes.offsite.system;
  const zmsAm = operativeSystemTypes.zmsAm.system;
  return [ccp, zmsAm, creativeFactory, offsite].includes(operativeSystem);
}

const TooltipLabel = getTooltipLabelWithText(HELP_TEXTS);

function ProductLine({
  label,
  name,
  business,
  product,
  countries,
  onChange,
  id,
  budget,
  netClientBudget,
  netClientBudgetExternal,
  wkzBudget,
  coInvest,
  freeService,
  minStartDate,
  maxEndDate,
  isMerchantExclusivityEditable,
  expanded,
  offSiteMinBudgetSpecification,
  isWholesale,
  isHybrid,
  isPureGrowthCampaign,
  isSelectionBoostCampaign,
  excludeDiscountedSKUs,
  promoteOnlyDiscountedSKUs,
  merchantExclusivity,
  filterOnSupplierCode,
  highlightedSkus,
  compact,
  spentBudget,
  status,
  steerOnSupplierCode,
  productMeta,
  linkToDownstreamSystem,
  outfitIds,
  opsHandoverCompliance,
  offerType,
  lines,
  onChangeCampaignLine,
  onChangeCampaign,
  campaignSave,
}) {
  const {
    code: productCode,
    isZmsAmPushable,
    supportsAdManager,
    supportsCreativeCollabaryId,
    supportsCreativeProductionBudgetBreakdown,
    supportsCreativeEnhancedProductShootingBudgetBreakdown,
    supportsCreativeLandingPageBudgetBreakdown,
    supportsNumberOfURLs,
    supportsHighlightedSKUs,
    supportsOutfitIds,
    supportsCollectionIds,
    operativeSystem,
    supportsOfferTypes,
    supportsContentIds,
  } = product;
  const {
    index,
    creativeCollabaryId,
    creativeCampaignSetup,
    creativeConcept,
    creativeHandlingFee,
    creativeTalent,
    creativeDesign,
    creativeReach,
    creativeKsk,
    costPerShootingDay,
    additionalModelCost,
    videoUpgrade,
    locationUpgrade,
    advancedUsageRights,
    numberOfShootingDays,
    landingPageCost,
    mastercopyCost,
    numberOfURLs,
    useCollectionIds,
    useContentIds,
    proposition,
  } = productMeta || {};

  const [overCCPLimit, setOverCCPLimit] = useState(false);
  const [originalPricing, setOriginalPricing] = useState({});

  const isDraftCampaignEnabled = useSafeFlag(FEATURE_FLAGS.DRAFT_CAMPAIGN);
  const OPERATIVE_SYSTEM_TYPES = getOperativeSystem(isDraftCampaignEnabled);

  // This function stores the initial pricing of the lines. With this, the pricing model of each line
  // can go back to how it was in case the offerType is changed again to other than HPTO or M1TO
  const initializeOriginalPricing = () => {
    const pricingMap = {};
    lines.forEach(item => {
      pricingMap[item.id] = item.pricing;
    });
    setOriginalPricing(pricingMap);
  };
  useEffect(() => {
    initializeOriginalPricing();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lineTitle = (
    <CampaignLineHeaderActions
      label={label}
      expanded={expanded}
      countries={countries}
      isProduct
      externalLink={linkToDownstreamSystem}
    />
  );
  const isPropositionAllowed = isOfferTypeAllowingPropositions(offerType);

  const allowContentIds = isContentIdsAllowed(
    supportsContentIds,
    proposition,
    offerType,
    productCode,
  );

  const allowCollectionIds = isCollectionIdsAllowed(
    supportsCollectionIds,
    supportsCreativeProductionBudgetBreakdown,
    proposition,
    offerType,
  );

  const isCountryLineDisabled = useCollectionIds || useContentIds;
  const countryLines = renderCountryLines(
    expanded,
    countries,
    name,
    onChange,
    isCountryLineDisabled,
  );

  if (compact) {
    return (
      <>
        <div className="c-campaign-line c-campaign-line--level-2">
          <Row>
            <Col sm={3}>{lineTitle}</Col>
          </Row>
        </div>
        {countryLines}
      </>
    );
  }

  const campaignLineIds = countries.reduce(
    (acc, cur) => acc.concat(cur.lines),
    [],
  );

  const hasPushButton = shouldRenderPushButton(
    operativeSystem,
    isDraftCampaignEnabled,
  );
  const isZmsAm = operativeSystem === OPERATIVE_SYSTEM_TYPES.ccp.system;

  const ccp = isCCP({ code: productCode });

  const isHighlightedSKUDisabled =
    useCollectionIds ||
    useContentIds ||
    status === campaignLineStatuses.PUSHED ||
    status === campaignLineStatuses.LOCKED;

  const isPushed = status === campaignLineStatuses.PUSHED;

  const isSteerOnSupplierCodeDisplayed = !(isWholesale || isHybrid);

  const { READY, PUSHED } = campaignLineStatuses;

  const productCodeIsM1 = [
    productCodes.M1_APP_LABEL,
    productCodes.M1_WEB_LABEL,
    productCodes.M1_WEB,
  ].includes(productCode);

  const handleOfferTypeChange = event => {
    const { value } = event.target;

    lines.forEach(item => {
      onChangeCampaignLine({
        target: {
          name: `${item.id}.offerType`,
          value,
        },
      });
    });

    if (value === offerTypes.HPTO) {
      onChangeCampaign({
        target: { name: 'useRecommendedColors', value: true },
      });
    }

    lines.forEach(item => {
      onChangeCampaignLine({
        target: {
          name: `${item.id}.pricing`,
          value:
            value === offerTypes.HPTO ||
            value === offerTypes.M1TO ||
            (value === offerTypes.CTO && productCodeIsM1)
              ? PricingModels.FEE_FLAT
              : originalPricing[item.id],
        },
      });
    });
  };

  return (
    <>
      <div className="c-campaign-line c-campaign-line--level-2">
        <Row>
          <Col sm={3}>
            {lineTitle}
            {[READY, PUSHED].includes(status) && (
              <div className="ml-5">
                <ComplianceStatus compliance={opsHandoverCompliance} />
              </div>
            )}
          </Col>
          <Col sm={2}>
            <div className="d-flex align-items-center">
              <CurrencyFormat
                className="c-campaign-line__value mr-2"
                value={budget}
                text
              />
              <BudgetDetailsPopover
                id={id}
                freeService={freeService}
                netClientBudget={netClientBudget}
                netClientBudgetExternal={netClientBudgetExternal}
                wkzBudget={wkzBudget}
                coInvest={coInvest}
                identifier="product-line"
              />
              <span className="ml-2 small text-muted text-nowrap">
                <CurrencyFormat value={spentBudget} text /> spent
              </span>
            </div>
            {isOffsite({ business }) && (
              <div className="c-campaign-line__label">
                <OffSiteMinBudgetCalculator
                  id={id}
                  specification={offSiteMinBudgetSpecification}
                  startDate={minStartDate}
                  endDate={maxEndDate}
                  countriesCount={countries.length}
                  serviceValue={budget}
                  layout="row"
                />
              </div>
            )}
          </Col>
          <Col sm={2}>
            <DateRange
              startName={`product-${id}.startDate`}
              endName={`product-${id}.endDate`}
              startDate={minStartDate}
              endDate={maxEndDate}
              small
              readOnly
              text
            />
          </Col>
          <Col sm={5} className="d-flex align-items-baseline">
            {(!isDraftCampaignEnabled || !isZmsAm) &&
            !supportsCreativeCollabaryId &&
            !supportsAdManager ? (
              <CreativeAssociations productId={id} countries={countries} />
            ) : (
              <div
                className="ml-2"
                style={{ width: '100px', height: '23px' }}
              />
            )}
            <div className="ml-2">
              <ProductStatusChangeButton
                campaignLineIds={campaignLineIds}
                productCode={productCode}
                onChange={onChange}
                highlightedSkus={highlightedSkus}
                campaignSave={campaignSave}
                overCCPLimit={isDraftCampaignEnabled ? false : overCCPLimit}
              />
            </div>
            {hasPushButton && (
              <div className="ml-2">
                <CampaignLinePushButton
                  type={Object.keys(OPERATIVE_SYSTEM_TYPES).find(
                    key =>
                      OPERATIVE_SYSTEM_TYPES[key].system === operativeSystem,
                  )}
                  productCode={productCode}
                  overCCPLimit={isDraftCampaignEnabled ? false : overCCPLimit}
                  isZmsAmPushable={
                    isDraftCampaignEnabled ? false : isZmsAmPushable
                  }
                  productLabel={label}
                  onChange={onChange}
                  highlightedSkus={highlightedSkus}
                  campaignSave={campaignSave}
                />
              </div>
            )}
            {!hasPushButton && (
              <div className="ml-2">
                <ProductLockButton campaignLineIds={campaignLineIds} />
              </div>
            )}
            {supportsCreativeCollabaryId && (
              <Label
                for={`productMetas[${index}].creativeCollabaryId`}
                className="c-campaign-line__label ml-3 d-inline"
              >
                C-ID
                <Input
                  id="cl-creativeCollabaryId"
                  name={`productMetas[${index}].creativeCollabaryId`}
                  value={creativeCollabaryId}
                  className="d-inline ml-2 w-auto"
                  onChange={onChange}
                />
              </Label>
            )}
            <div className="ml-auto">
              <CampaignLineStatus status={status} />
            </div>
          </Col>
        </Row>
        {supportsCreativeProductionBudgetBreakdown && (
          <CreativeBudgetBreakdown
            index={index}
            creativeCampaignSetup={creativeCampaignSetup}
            creativeConcept={creativeConcept}
            creativeHandlingFee={creativeHandlingFee}
            creativeTalent={creativeTalent}
            creativeDesign={creativeDesign}
            creativeReach={creativeReach}
            creativeKsk={creativeKsk}
            onChange={onChange}
          />
        )}
        {supportsCreativeEnhancedProductShootingBudgetBreakdown && (
          <CreativeEnhancedProductShootingBudgetBreakdown
            index={index}
            costPerShootingDay={costPerShootingDay}
            additionalModelCost={additionalModelCost}
            videoUpgrade={videoUpgrade}
            locationUpgrade={locationUpgrade}
            advancedUsageRights={advancedUsageRights}
            numberOfShootingDays={numberOfShootingDays}
            onChange={onChange}
          />
        )}
        {supportsCreativeLandingPageBudgetBreakdown && (
          <CreativeLandingPageBudgetBreakdown
            index={index}
            landingPageCost={landingPageCost}
            mastercopyCost={mastercopyCost}
            onChange={onChange}
          />
        )}
        {supportsNumberOfURLs &&
          renderNumberOfURL(index, 'numberOfURLs', numberOfURLs, onChange)}
        {supportsOfferTypes && (
          <Col sm={3}>
            <FormGroup className="align-items-center ml-5 mr-1">
              <Label for="ca-offerType">Offer Type</Label>
              <SingleSelect
                options={offerTypeOptions}
                id="ca-offerType"
                name={`${[lines[0].id]}.offerType`}
                value={offerType}
                onChange={handleOfferTypeChange}
                readOnly={
                  isDraftCampaignEnabled ? isPushed : isPushed || isZmsAm
                }
              />
            </FormGroup>
          </Col>
        )}
        {supportsContentIds && isPropositionAllowed && (
          <Col sm={3}>
            <FormGroup className="align-items-center ml-5 mr-1">
              <Label for="ca-proposition">Proposition</Label>
              <SingleSelect
                options={propositionOptions}
                id="ca-proposition"
                name={`productMetas[${index}].proposition`}
                value={proposition}
                onChange={onChange}
                readOnly={
                  isDraftCampaignEnabled ? isPushed : isPushed || isZmsAm
                }
              />
            </FormGroup>
          </Col>
        )}
        {!isDraftCampaignEnabled && ccp && (
          <Row className="align-items-baseline mt-3">
            <Col sm={3} />
            {isMerchantExclusivityEditable && (
              <Col sm={3}>
                <FormGroup inline row className="align-items-center">
                  <TooltipLabel
                    text="Merchant Exclusivity"
                    helpKey="merchantExclusivity"
                    section="header"
                    resource="ca"
                  />
                  <Col>
                    <SingleSelect
                      options={merchantExclusivityOptions}
                      id="ca-merchantExclusivity"
                      name="merchantExclusivity"
                      value={merchantExclusivity}
                      onChange={onChange}
                      readOnly={isPushed}
                    />
                  </Col>
                </FormGroup>
              </Col>
            )}
            {isPureGrowthCampaign ? (
              <>
                <Col sm={2}>
                  <FormGroup>
                    <Input
                      type="checkbox"
                      id="ca-excludeDiscountedSKUs"
                      name="excludeDiscountedSKUs"
                      checked={excludeDiscountedSKUs}
                      onChange={onChange}
                      sm={1}
                    />
                    <Label check for="ca-excludeDiscountedSKUs">
                      Exclude Discounted SKUs
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      type="checkbox"
                      id="ca-promoteOnlyDiscountedSKUs"
                      name="promoteOnlyDiscountedSKUs"
                      checked={promoteOnlyDiscountedSKUs}
                      onChange={onChange}
                      sm={1}
                    />
                    <Label check for="ca-promoteOnlyDiscountedSKUs">
                      Push Discounted SKUs only
                    </Label>
                  </FormGroup>
                </Col>
                <Col sm={2}>
                  <FormGroup>
                    <Input
                      type="checkbox"
                      id="ca-filterOnSupplierCode"
                      name="filterOnSupplierCode"
                      checked={filterOnSupplierCode}
                      onChange={onChange}
                      sm={1}
                    />
                    <Label check for="ca-filterOnSupplierCode">
                      Filter on Supplier Code
                    </Label>
                  </FormGroup>
                </Col>
              </>
            ) : (
              <>
                <Col sm={2}>
                  {isSteerOnSupplierCodeDisplayed && (
                    <FormGroup>
                      <Input
                        type="checkbox"
                        id="ca-steerOnSupplierCode"
                        name="steerOnSupplierCode"
                        checked={steerOnSupplierCode}
                        onChange={onChange}
                        sm={1}
                      />
                      <Label check for="ca-steerOnSupplierCode">
                        Manually steer on Supplier Code
                      </Label>
                    </FormGroup>
                  )}
                  {isSelectionBoostCampaign && (
                    <FormGroup>
                      <Input
                        type="checkbox"
                        id="ca-promoteOnlyDiscountedSKUs"
                        name="promoteOnlyDiscountedSKUs"
                        checked={promoteOnlyDiscountedSKUs}
                        onChange={onChange}
                        sm={1}
                      />
                      <Label check for="ca-promoteOnlyDiscountedSKUs">
                        Push Discounted SKUs only
                      </Label>
                    </FormGroup>
                  )}
                </Col>
                <Col sm={4} className={`align-self-start ${productCode}-sku`}>
                  {renderHighlightedSKUsInput(
                    productCode,
                    highlightedSkus,
                    onChange,
                    isHighlightedSKUDisabled,
                    true,
                    ccp,
                    setOverCCPLimit,
                    isDraftCampaignEnabled,
                  )}
                  <Label className="c-campaign-line__label">
                    Highlighted SKUs*
                  </Label>
                  <HighlightedSkusWarning
                    highlightedSkus={highlightedSkus}
                    productCode={productCode}
                  />
                </Col>
              </>
            )}
          </Row>
        )}
        {(isDraftCampaignEnabled
          ? !isZmsAm && supportsHighlightedSKUs
          : !ccp && supportsHighlightedSKUs) && (
          <Row className="align-items-baseline mt-3">
            <Col sm={3} />
            <Col sm={5} className={`align-self-start ${productCode}-sku`}>
              {renderHighlightedSKUsInput(
                productCode,
                highlightedSkus,
                onChange,
                isHighlightedSKUDisabled,
                true,
                false,
                setOverCCPLimit,
                isDraftCampaignEnabled,
              )}
              <Label className="c-campaign-line__label">Highlighted SKUs</Label>
              <HighlightedSkusWarning
                highlightedSkus={highlightedSkus}
                productCode={productCode}
              />
            </Col>
          </Row>
        )}
        {supportsOutfitIds && (
          <Row className="align-items-baseline mt-3">
            <Col sm={3} />
            <Col sm={5} className="align-self-start">
              <TagInput
                name="outfitIds"
                value={outfitIds}
                onChange={onChange}
                allowCopy
              />
              <Label className="c-campaign-line__label">Outfit Ids</Label>
            </Col>
          </Row>
        )}
        {allowCollectionIds && (
          <CollectionIds productMeta={productMeta} onChange={onChange} />
        )}

        {allowContentIds && (
          <Row
            key={`alert-${productCode}`}
            className="align-items-baseline mt-3"
          >
            <Col sm={3} />
            <Col sm={5} className="align-self-start">
              <Alert
                className="px-2 py-1 font-size-small"
                color={COLORS.WARNING}
              >
                <GoInfo size="1.5em" /> To add creatives assign content entry
                IDs in the tab of the creative upload modal.
              </Alert>
            </Col>
          </Row>
        )}
      </div>
      {(operativeSystem !== OPERATIVE_SYSTEM_TYPES.zmsAm.system || ccp) &&
        countryLines}
    </>
  );
}

ProductLine.propTypes = {
  label: string.isRequired,
  business: string.isRequired,
  product: shape({
    code: string.isRequired,
    isZmsAmPushable: bool.isRequired,
    supportsAdManager: bool,
    supportsCreativeCollabaryId: bool.isRequired,
    supportsCreativeProductionBudgetBreakdown: bool.isRequired,
    supportsCreativeEnhancedProductShootingBudgetBreakdown: bool.isRequired,
    supportsCreativeLandingPageBudgetBreakdown: bool.isRequired,
    supportsNumberOfURLs: bool.isRequired,
    supportsHighlightedSKUs: bool.isRequired,
    supportsOutfitIds: bool.isRequired,
    supportsCollectionIds: bool.isRequired,
    operativeSystem: oneOf(
      Object.values(operativeSystemsTypes).map(({ system }) => system),
    ).isRequired,
  }).isRequired,
  id: number.isRequired,
  countries: arrayOf(clCountryLineShape),
  name: string.isRequired,
  onChange: func,
  budget: number.isRequired,
  spentBudget: number.isRequired,
  netClientBudget: number.isRequired,
  netClientBudgetExternal: number.isRequired,
  wkzBudget: number.isRequired,
  coInvest: number.isRequired,
  freeService: number.isRequired,
  minStartDate: string,
  maxEndDate: string,
  isMerchantExclusivityEditable: bool,
  offSiteMinBudgetSpecification: offSiteMinBudgetShape,
  isPureGrowthCampaign: bool,
  isSelectionBoostCampaign: bool,
  isWholesale: bool,
  isHybrid: bool,
  excludeDiscountedSKUs: bool,
  promoteOnlyDiscountedSKUs: bool,
  merchantExclusivity: string,
  filterOnSupplierCode: bool.isRequired,
  highlightedSkus: highlightedSkusShape.isRequired,
  expanded: bool,
  compact: bool.isRequired,
  status: oneOf(Object.values(campaignLineStatuses)).isRequired,
  steerOnSupplierCode: bool,
  productMeta: campaignProductMetaShape,
  linkToDownstreamSystem: string,
  outfitIds: arrayOf(string).isRequired,
  opsHandoverCompliance: string,
  offerType: string,
  lines: arrayOf(object), // eslint-disable-line react/forbid-prop-types,
  onChangeCampaignLine: func.isRequired,
  onChangeCampaign: func.isRequired,
  campaignSave: func.isRequired,
};

ProductLine.defaultProps = {
  countries: [],
  onChange: undefined,
  minStartDate: null,
  maxEndDate: null,
  isMerchantExclusivityEditable: false,
  offSiteMinBudgetSpecification: {},
  isPureGrowthCampaign: false,
  isSelectionBoostCampaign: false,
  isWholesale: false,
  isHybrid: false,
  excludeDiscountedSKUs: false,
  promoteOnlyDiscountedSKUs: false,
  merchantExclusivity: '',
  steerOnSupplierCode: false,
  expanded: false,
  productMeta: undefined,
  linkToDownstreamSystem: undefined,
  opsHandoverCompliance: complianceStatuses.NONE,
  offerType: undefined,
  lines: [],
};

export default ProductLine;
